// =====================================================
// ODONTOGRAM CHILD - Odontograma infantil (20 dientes)
// Basado en el diseño premium proporcionado
// =====================================================
import type { SelectionMode } from './OdontogramModal';

interface OdontogramChildProps {
  selectedTeeth: Set<string>;
  onToothClick: (toothNumber: string) => void;
  selectionMode: SelectionMode;
}

// SVG paths para diferentes tipos de dientes
const TOOTH_PATHS = {
  molar: 'M5,15 Q5,5 15,5 L25,5 Q35,5 35,15 L35,35 Q35,45 20,45 Q5,45 5,35 Z',
  canine: 'M12,20 Q20,2 28,20 L28,40 Q28,45 20,45 Q12,45 12,40 Z',
  incisor: 'M10,15 L30,15 L30,40 Q30,46 20,46 Q10,46 10,40 Z',
};

// Configuración de dientes infantiles por posición
const TEETH_CONFIG: Record<string, { type: keyof typeof TOOTH_PATHS }> = {
  // Cuadrante 1 (Superior derecho)
  '55': { type: 'molar' },
  '54': { type: 'molar' },
  '53': { type: 'canine' },
  '52': { type: 'incisor' },
  '51': { type: 'incisor' },
  // Cuadrante 2 (Superior izquierdo)
  '61': { type: 'incisor' },
  '62': { type: 'incisor' },
  '63': { type: 'canine' },
  '64': { type: 'molar' },
  '65': { type: 'molar' },
  // Cuadrante 3 (Inferior izquierdo)
  '71': { type: 'incisor' },
  '72': { type: 'incisor' },
  '73': { type: 'canine' },
  '74': { type: 'molar' },
  '75': { type: 'molar' },
  // Cuadrante 4 (Inferior derecho)
  '81': { type: 'incisor' },
  '82': { type: 'incisor' },
  '83': { type: 'canine' },
  '84': { type: 'molar' },
  '85': { type: 'molar' },
};

const UPPER_RIGHT = ['55', '54', '53', '52', '51'];
const UPPER_LEFT = ['61', '62', '63', '64', '65'];
const LOWER_RIGHT = ['85', '84', '83', '82', '81'];
const LOWER_LEFT = ['71', '72', '73', '74', '75'];

interface ToothProps {
  number: string;
  isSelected: boolean;
  onClick: () => void;
  isLower?: boolean;
  isClickable: boolean;
}

function Tooth({ number, isSelected, onClick, isLower, isClickable }: ToothProps) {
  const config = TEETH_CONFIG[number];
  const path = TOOTH_PATHS[config.type];

  return (
    <div
      onClick={isClickable ? onClick : undefined}
      className={`
        relative flex flex-col items-center transition-all duration-200
        ${isClickable ? 'cursor-pointer' : 'cursor-default'}
        ${isSelected ? 'scale-108' : ''}
        group
      `}
      style={{ width: '42px' }}
    >
      {/* Número arriba para dientes inferiores */}
      {isLower && (
        <span
          className={`text-[10px] font-bold mb-1 transition-colors ${
            isSelected ? 'text-blue-500' : 'text-gray-400'
          }`}
        >
          {number}
        </span>
      )}

      {/* SVG del diente */}
      <div
        className={`
          w-10 h-12 flex items-center justify-center transition-all duration-300
          ${isClickable ? 'group-hover:-translate-y-0.5' : ''}
        `}
        style={{
          filter: isSelected
            ? 'drop-shadow(0 0 10px rgba(59, 130, 246, 0.7))'
            : 'drop-shadow(0 2px 4px rgba(0,0,0,0.05))',
        }}
      >
        <svg
          className={`w-full h-full ${isLower ? 'rotate-180' : ''}`}
          viewBox="0 0 40 50"
        >
          <defs>
            <linearGradient id={`childToothGrad-${number}`} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#FFFFFF', stopOpacity: 1 }} />
              <stop offset="60%" style={{ stopColor: '#FDFCF0', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: '#F3EFE0', stopOpacity: 1 }} />
            </linearGradient>
          </defs>
          <path
            d={path}
            fill={`url(#childToothGrad-${number})`}
            stroke={isSelected ? '#3B82F6' : '#E2E8F0'}
            strokeWidth={isSelected ? 2 : 1}
          />
          {/* Indicador de selección */}
          {isSelected && (
            <circle
              cx="20"
              cy="25"
              r="5"
              fill="#3B82F6"
              fillOpacity="0.3"
              stroke="#3B82F6"
              strokeWidth="1.5"
            />
          )}
        </svg>
      </div>

      {/* Número abajo para dientes superiores */}
      {!isLower && (
        <span
          className={`text-[10px] font-bold mt-1 transition-colors ${
            isSelected ? 'text-blue-500' : 'text-gray-400'
          }`}
        >
          {number}
        </span>
      )}
    </div>
  );
}

export function OdontogramChild({
  selectedTeeth,
  onToothClick,
  selectionMode,
}: OdontogramChildProps) {
  const isClickable = selectionMode === 'UNIT';

  return (
    <div className="w-full max-w-3xl space-y-6">
      {/* Indicador de paciente pediátrico */}
      <div className="flex justify-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-cyan-50 border border-cyan-200 rounded-full">
          <span className="text-lg">👶</span>
          <span className="text-xs font-bold text-cyan-700 uppercase tracking-wide">
            Dentición Temporal
          </span>
        </div>
      </div>

      {/* Arcada Superior */}
      <div className="flex justify-center gap-4">
        {/* Cuadrante 1 (Superior Derecho) */}
        <div className="flex gap-2 pr-4 border-r-2 border-gray-100 items-end">
          {UPPER_RIGHT.map((num) => (
            <Tooth
              key={num}
              number={num}
              isSelected={selectedTeeth.has(num)}
              onClick={() => onToothClick(num)}
              isClickable={isClickable}
            />
          ))}
        </div>

        {/* Cuadrante 2 (Superior Izquierdo) */}
        <div className="flex gap-2 pl-4 items-end">
          {UPPER_LEFT.map((num) => (
            <Tooth
              key={num}
              number={num}
              isSelected={selectedTeeth.has(num)}
              onClick={() => onToothClick(num)}
              isClickable={isClickable}
            />
          ))}
        </div>
      </div>

      {/* Línea media */}
      <div className="flex justify-center">
        <div className="h-px bg-gray-200 w-full max-w-2xl relative">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-6 py-1 border border-gray-100 rounded-full text-[10px] font-bold text-gray-400 uppercase tracking-widest">
            Línea Media
          </div>
        </div>
      </div>

      {/* Arcada Inferior */}
      <div className="flex justify-center gap-4">
        {/* Cuadrante 4 (Inferior Derecho) */}
        <div className="flex gap-2 pr-4 border-r-2 border-gray-100 items-start">
          {LOWER_RIGHT.map((num) => (
            <Tooth
              key={num}
              number={num}
              isSelected={selectedTeeth.has(num)}
              onClick={() => onToothClick(num)}
              isLower
              isClickable={isClickable}
            />
          ))}
        </div>

        {/* Cuadrante 3 (Inferior Izquierdo) */}
        <div className="flex gap-2 pl-4 items-start">
          {LOWER_LEFT.map((num) => (
            <Tooth
              key={num}
              number={num}
              isSelected={selectedTeeth.has(num)}
              onClick={() => onToothClick(num)}
              isLower
              isClickable={isClickable}
            />
          ))}
        </div>
      </div>

      {/* Leyenda de cuadrantes */}
      <div className="flex justify-center gap-6 mt-4 text-xs text-gray-400">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded bg-cyan-100 flex items-center justify-center font-bold text-cyan-600 text-[10px]">
            Q1
          </div>
          <span>Sup. Derecho</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded bg-cyan-100 flex items-center justify-center font-bold text-cyan-600 text-[10px]">
            Q2
          </div>
          <span>Sup. Izquierdo</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded bg-cyan-100 flex items-center justify-center font-bold text-cyan-600 text-[10px]">
            Q4
          </div>
          <span>Inf. Derecho</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded bg-cyan-100 flex items-center justify-center font-bold text-cyan-600 text-[10px]">
            Q3
          </div>
          <span>Inf. Izquierdo</span>
        </div>
      </div>
    </div>
  );
}
